import { useState } from 'react';
import { useApp } from '../../components/AppProvider';
import { Deal } from '../../lib/types';

interface UseSignatureSharingResult {
  selectedTemplateId: string;
  setSelectedTemplateId: React.Dispatch<React.SetStateAction<string>>;
  generatingSignatureLink: boolean;
  signatureLink: string;
  shareMethod: 'copy' | 'whatsapp' | 'email';
  setShareMethod: React.Dispatch<React.SetStateAction<'copy' | 'whatsapp' | 'email'>>;
  signatureLinkCopied: boolean;
  handleGenerateSignatureLink: (
    deal: Deal | null, 
    generateSignatureUrl: Function, 
    updateDeal: Function, 
    templates: any[], 
    onSuccess: () => void
  ) => Promise<void>;
  handleShareSignatureLink: (onSuccess?: () => void) => Promise<void>;
  handleCopySignatureLink: (signatureRequestUrl?: string) => Promise<void>;
}

export const useSignatureSharing = (translations: any): UseSignatureSharingResult => {
  const { toast } = useApp();
  
  // Template and signature states
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [generatingSignatureLink, setGeneratingSignatureLink] = useState(false);
  const [signatureLink, setSignatureLink] = useState<string>('');
  const [shareMethod, setShareMethod] = useState<'copy' | 'whatsapp' | 'email'>('copy');
  const [signatureLinkCopied, setSignatureLinkCopied] = useState(false);

  // Handler for generating signature link
  const handleGenerateSignatureLink = async (
    deal: Deal | null,
    generateSignatureUrl: Function,
    updateDeal: Function,
    templates: any[],
    onSuccess: () => void
  ) => {
    if (!deal?.client_id) {
      toast.error('Client information is required');
      return;
    }
    
    if (!selectedTemplateId) {
      toast.error('Please select a template first');
      return;
    }
    
    setGeneratingSignatureLink(true);
    try {
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      
      if (!selectedTemplate) {
        toast.error('Selected template not found');
        return;
      }
      
      // Generate signature URL
      const signatureUrlId = await generateSignatureUrl(
        deal.id, 
        deal.client_id,
        selectedTemplateId
      );
      
      if (!signatureUrlId) {
        toast.error('Failed to generate signature URL');
        return;
      }
      
      // Store the generated link
      const baseUrl = window.location.origin;
      const fullSignatureUrl = `${baseUrl}/sign/${signatureUrlId}`;
      setSignatureLink(fullSignatureUrl);
      
      // Update deal status
      await updateDeal(deal.id, { 
        status: 'sent',
        signature_request_url: signatureUrlId,
        template_id: selectedTemplateId
      });
      
      toast.success(translations.signature?.generationSuccess || 'Signature link generated successfully');
      
      // Call onSuccess callback provided by parent component
      onSuccess();
      
    } catch (error) {
      console.error('Error generating signature link:', error);
      toast.error(translations.signature?.generationError + (error instanceof Error ? ': ' + error.message : ''));
    } finally {
      setGeneratingSignatureLink(false);
    }
  };

  // Handle sharing the signature link
  const handleShareSignatureLink = async (onSuccess?: () => void) => {
    if (!signatureLink) {
      toast.error('No signature link generated');
      return;
    }
    
    try {
      switch (shareMethod) {
        case 'copy':
          await navigator.clipboard.writeText(signatureLink);
          setSignatureLinkCopied(true);
          setTimeout(() => setSignatureLinkCopied(false), 3000);
          toast.success(translations.signature?.linkCopied || 'Signature link copied to clipboard');
          break;
          
        case 'whatsapp':
          const whatsappMessage = translations.signature?.whatsappMessage || 'Please sign the document at the following link:';
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
            `${whatsappMessage} ${signatureLink}`
          )}`;
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
          toast.success('WhatsApp opened with signature link');
          break;
          
        case 'email':
          const emailSubject = encodeURIComponent(translations.signature?.emailSubject || 'Document for signature');
          const emailMessage = translations.signature?.emailMessage || 'Please sign the document at the following link:';
          const emailBody = encodeURIComponent(
            `${emailMessage}\n\n${signatureLink}`
          );
          window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
          toast.success('Email client opened with signature link');
          break;
      }
      
      // Call success callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error('Error sharing signature link:', error);
      toast.error('Failed to share signature link');
    }
  };

  // Copy signature link handler for existing deals
  const handleCopySignatureLink = async (signatureRequestUrl?: string) => {
    if (!signatureRequestUrl) return;
    
    try {
      // Construct the full signature URL
      const baseUrl = window.location.origin;
      const signatureUrl = `${baseUrl}/sign/${signatureRequestUrl}`;
      
      await navigator.clipboard.writeText(signatureUrl);
      setSignatureLinkCopied(true);
      setTimeout(() => setSignatureLinkCopied(false), 3000);
      toast.success('Signature link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy signature link');
    }
  };

  return {
    selectedTemplateId,
    setSelectedTemplateId,
    generatingSignatureLink,
    signatureLink,
    shareMethod,
    setShareMethod,
    signatureLinkCopied,
    handleGenerateSignatureLink,
    handleShareSignatureLink,
    handleCopySignatureLink
  };
};